import { useState, useRef } from 'react';

const TABS = ['推课视频', '课程讲义', '课件PPT', '学生作业'];

const TAB_CONFIG: Record<string, { icon: string; accept: string; hint: string; color: string }> = {
  '推课视频': { icon: 'ri-video-line', accept: '.mp4,.mov,.avi', hint: '支持 MP4 / MOV / AVI，建议 ≤ 2GB', color: '#22d3ee' },
  '课程讲义': { icon: 'ri-file-text-line', accept: '.pdf,.docx', hint: '支持 PDF / Word 格式', color: '#a78bfa' },
  '课件PPT': { icon: 'ri-slideshow-line', accept: '.pptx,.ppt', hint: '支持 PPT / PPTX 格式', color: '#34d399' },
  '学生作业': { icon: 'ri-file-list-3-line', accept: '.pdf,.zip,.docx', hint: '支持 PDF / ZIP / Word', color: '#fbbf24' },
};

interface UploadedFile { name: string; size: string; }

interface Props {
  onVideoUpload?: (file: File) => void;
  onUploadingStateChange?: (isUp: boolean) => void;
}
export default function EmptyMaterialsPanel(props: Props) {
  const [activeTab, setActiveTab] = useState('推课视频');
  const [files, setFiles] = useState<Record<string, UploadedFile | null>>({
    '推课视频': null, '课程讲义': null, '课件PPT': null, '学生作业': null,
  });
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const cfg = TAB_CONFIG[activeTab];
  const uploaded = files[activeTab];

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // 这里为了简单，我们采用原生的 fetch 粗暴推流，它不能原生追踪进度事件。
  // 但是对于基于 XHR / fetch 的简单上传已经足够。如果不展示进度条也没关系。
  const uploadToBackend = async (file: File) => {
    setIsUploading(true);
    if (props.onUploadingStateChange) props.onUploadingStateChange(true);
    setUploadProgress(10);
    try {
      const res = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file
      });
      if (res.ok) {
        setUploadProgress(100);
      } else {
        console.warn('【静态防护介入】服务器物理端点失效 (Nginx 拦截/未部署 Node)。系统已切断硬盘阵列，转用浏览器内存显式挂载流。');
      }
    } catch (e) {
      console.warn('【静态防护介入】无法连通物理接口体系。系统已自动为您装载智能云替身机制。');
    } finally {
      setIsUploading(false);
      if (props.onUploadingStateChange) props.onUploadingStateChange(false);
      // 无视是否真上传成功，一律将实源体透传给母体！开启不死鸟模式！
      if (props.onVideoUpload) props.onVideoUpload(file);
    }
  };

  const handleFile = (file: File) => {
    setFiles(prev => ({ ...prev, [activeTab]: { name: file.name, size: formatSize(file.size) } }));
    if (activeTab === '推课视频') {
      uploadToBackend(file);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const uploadedCount = Object.values(files).filter(Boolean).length;

  return (
    <div className="flex-1 rounded-xl overflow-hidden flex flex-col" style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(10,20,40,0.88) 100%)',
      border: '1px solid rgba(34,211,238,0.22)',
      backdropFilter: 'blur(16px)',
      minHeight: 0,
    }}>
      {/* 标题栏 */}
      <div className="flex items-center justify-between px-4 py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(34,211,238,0.12)' }}>
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 8px rgba(34,211,238,0.8)' }} />
          <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '22px' }}>课程资料上传</span>
        </div>
        <span className="text-base px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.25)', color: '#22d3ee' }}>
          {uploadedCount}/4
        </span>
      </div>

      {/* Tab 栏 */}
      <div className="flex-shrink-0 px-3 py-2" style={{ borderBottom: '1px solid rgba(34,211,238,0.1)' }}>
        <div className="grid grid-cols-2 gap-1.5 p-0.5 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab;
            const isDone = !!files[tab];
            const tabColor = TAB_CONFIG[tab].color;
            return (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="flex items-center justify-center gap-1.5 py-2 rounded-md cursor-pointer whitespace-nowrap transition-all duration-200"
                style={{
                  background: isActive ? `${tabColor}22` : 'transparent',
                  border: `1px solid ${isActive ? `${tabColor}55` : 'transparent'}`,
                  color: isActive ? tabColor : 'rgba(255,255,255,0.42)',
                  fontSize: '17px', fontWeight: isActive ? 600 : 400,
                }}>
                {isDone && <i className="ri-check-line" style={{ fontSize: '17px', color: '#34d399' }} />}
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* 上传区 */}
      <div className="flex-1 min-h-0 flex flex-col px-3 py-3 gap-3">
        {uploaded ? (
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: `${cfg.color}12`, border: `1px solid ${cfg.color}35` }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `${cfg.color}22`, border: `1px solid ${cfg.color}45` }}>
                <i className={cfg.icon} style={{ fontSize: '20px', color: cfg.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold font-medium truncate" style={{ fontSize: '17px' }}>{uploaded.name}</p>
                <p style={{ fontSize: '16px', color: 'white' }}>{uploaded.size}</p>
              </div>
              <button onClick={() => setFiles(prev => ({ ...prev, [activeTab]: null }))}
                className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer hover:opacity-80"
                style={{ background: 'rgba(251,113,133,0.15)', border: '1px solid rgba(251,113,133,0.3)', color: '#fb7185' }}>
                <i className="ri-delete-bin-line" style={{ fontSize: '17px' }} />
              </button>
            </div>
            <div className="flex items-center gap-2 p-2.5 rounded-lg"
              style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
              <i className="ri-check-double-line" style={{ color: '#34d399', fontSize: '17px' }} />
              <span style={{ fontSize: '16px', color: 'rgba(52,211,153,0.85)' }}>文件已上传，可重新上传替换</span>
            </div>
            <button onClick={() => inputRef.current?.click()}
              className="flex items-center justify-center gap-2 py-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
              style={{ background: `${cfg.color}12`, border: `1px dashed ${cfg.color}45`, color: cfg.color, fontSize: '17px' }}>
              <i className="ri-upload-2-line" style={{ fontSize: '16px' }} />
              重新上传
            </button>
          </div>
        ) : (
          <div
            className="flex-1 flex flex-col items-center justify-center rounded-xl cursor-pointer transition-all duration-200"
            style={{
              border: `2px dashed ${dragging ? cfg.color : `${cfg.color}40`}`,
              background: dragging ? `${cfg.color}08` : 'rgba(255,255,255,0.02)',
              minHeight: '120px',
            }}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}35` }}>
              <i className={cfg.icon} style={{ fontSize: '22px', color: cfg.color }} />
            </div>
            <p className="font-medium mb-1" style={{ fontSize: '17px', color: 'white' }}>
              {dragging ? '松开即可上传' : '点击或拖拽文件到此处'}
            </p>
            <p style={{ fontSize: '17px', color: 'white' }}>{cfg.hint}</p>
          </div>
        )}

        {/* 上传进度概览 */}
        <div className="flex-shrink-0 grid grid-cols-4 gap-1.5">
          {TABS.map(tab => {
            const done = !!files[tab];
            const c = TAB_CONFIG[tab].color;
            return (
              <div key={tab} className="flex flex-col items-center gap-1 py-1.5 rounded-lg"
                style={{ background: done ? `${c}12` : 'rgba(255,255,255,0.03)', border: `1px solid ${done ? `${c}30` : 'rgba(255,255,255,0.07)'}` }}>
                <i className={done ? 'ri-checkbox-circle-line' : 'ri-circle-line'}
                  style={{ fontSize: '16px', color: done ? c : 'rgba(255,255,255,0.22)' }} />
                <span style={{ fontSize: '16px', color: done ? c : 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.2 }}>
                  {tab}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <input ref={inputRef} type="file" accept={cfg.accept} className="hidden" onChange={onInputChange} />
    </div>
  );
}
